import { useState } from "react";
import useAppStore from "src/stores/appStore";
import { assistantIdSelector, selectedAccountIdSelector } from "src/stores/selectors/appSelectors";
import { useFetchConfigReview } from "src/queries/hooks/configReview";
import { CHANGE_TYPES, KNOWLEDGE_GROUP_KEY, REVIEW_BASES, REVIEW_COPY, REVIEW_GATES, REVIEW_GROUPS } from "./reviewChanges.consts";
import { countChangeTypes, filterReviewGroups, getReviewGroups } from "./reviewChanges.utils";

const routeOf = (collection) => REVIEW_GROUPS.find(({ key }) => key === collection)?.route;

const useReviewChangesDialog = ({ open, gate }) => {
  const assistantId = useAppStore(assistantIdSelector);
  const accountId = useAppStore(selectedAccountIdSelector);
  const isBuildGate = gate === REVIEW_GATES.BUILD;
  const [base, setBase] = useState(isBuildGate ? REVIEW_BASES.BUILD : REVIEW_BASES.LIVE);
  const [changeFilters, setChangeFilters] = useState([]);

  // What the review needs besides itself: the specs of edited instances and the listings of changed collections
  // (to name resources like their cards and place knowledge rows under their knowledge source).
  const contextOf = (review) => ({
    instances: (review?.collections ?? [])
      .filter(({ changeType }) => changeType === CHANGE_TYPES.EDITED)
      .map(({ collection, specKey, key }) => ({ collection, route: routeOf(collection), specKey, key })),
    collections: [...new Set([
      ...(review?.collections ?? []).map(({ collection }) => collection),
      ...((review?.knowledge?.length ?? 0) > 0 ? [KNOWLEDGE_GROUP_KEY] : [])
    ])].filter(routeOf).map((collection) => ({ collection, route: routeOf(collection) }))
  });
  const reviewQuery = useFetchConfigReview(assistantId, base, { enabled: open, contextOf });
  const { review, specs, listings } = reviewQuery.data ?? {};

  // With no usable last build the server compares with live: "Last build" is shown unavailable.
  const lastBuildUnavailable = base === REVIEW_BASES.BUILD && !!review?.baseFallback;
  const shownBase = lastBuildUnavailable ? REVIEW_BASES.LIVE : base;
  // While the other base loads, the review on screen is still the previous one: what it shows follows its own base.
  const reviewBase = review?.base === REVIEW_BASES.BUILD && !review?.baseFallback ? REVIEW_BASES.BUILD : REVIEW_BASES.LIVE;
  // Knowledge rows can only be compared with live (a build keeps no copy of its knowledge), so the Last build
  // review leaves them out — its counts and "No changes since last build" are about the build alone.
  const showsKnowledge = reviewBase === REVIEW_BASES.LIVE && !review?.knowledgeOmitted;
  const reviewShown = review && !showsKnowledge ? { ...review, knowledge: [] } : review;

  const allGroups = reviewShown ? getReviewGroups({ review: reviewShown, specs, collections: listings, accountId, assistantId }) : [];
  const counts = countChangeTypes(allGroups);
  const groups = filterReviewGroups(allGroups, changeFilters);
  const { isLoading, isError } = reviewQuery;
  // Reloading on screen (the other base, or Try again): the current review stays, the action waits.
  const isRefreshing = reviewQuery.isFetching && !isLoading;
  const isEmpty = !isLoading && !isError && allGroups.length === 0;

  const baseOptions = [REVIEW_BASES.BUILD, REVIEW_BASES.LIVE].map((value) => REVIEW_COPY.BASE_OPTIONS[value]);
  const handleBaseChange = (label) => {
    const next = Object.keys(REVIEW_COPY.BASE_OPTIONS).find((value) => REVIEW_COPY.BASE_OPTIONS[value] === label);
    if (next) setBase(next);
  };

  const toggleChangeFilter = (changeType) => setChangeFilters((selected) => (
    selected.includes(changeType) ? selected.filter((type) => type !== changeType) : [...selected, changeType]
  ));

  return {
    base: shownBase,
    groups,
    counts,
    changeFilters,
    toggleChangeFilter,
    clearChangeFilters: () => setChangeFilters([]),
    isLoading,
    isError,
    isEmpty,
    showBaseSwitch: isBuildGate,
    baseOptions,
    selectedBaseOption: REVIEW_COPY.BASE_OPTIONS[shownBase],
    handleBaseChange,
    disabledBaseOptions: lastBuildUnavailable ? [REVIEW_COPY.BASE_OPTIONS[REVIEW_BASES.BUILD]] : [],
    baseOptionTooltips: lastBuildUnavailable ? { [REVIEW_COPY.BASE_OPTIONS[REVIEW_BASES.BUILD]]: REVIEW_COPY.LAST_BUILD_UNAVAILABLE } : {},
    // Knowledge never drops out silently: Last build mode says it's left out (even when it failed to load,
    // since there may be changes), and a user without access to knowledge sources is told they still ship.
    showKnowledgeBuildNote: reviewBase === REVIEW_BASES.BUILD && !review?.knowledgeOmitted
      && ((review?.knowledge?.length ?? 0) > 0 || !!review?.knowledgeError),
    showKnowledgeOmittedNote: !!review?.knowledgeOmitted,
    showLiveBase: () => setBase(REVIEW_BASES.LIVE),
    showKnowledgeError: showsKnowledge && !!review?.knowledgeError,
    retry: () => reviewQuery.refetch(),
    isRefreshing
  };
};

export default useReviewChangesDialog;
